import { map, Observable, of } from 'rxjs';
import {
  CustomFieldProps,
  DynamicField,
  DynamicForm,
  EditorFieldProps,
  FieldVariant,
  SelectOption,
  SwitchFieldProps,
  TextFieldProps,
} from '../../../../components/form-builder/form-builder.types';
import { AoRepository } from '@/store/ao-state/ao-state.repository';
import { PpmSlotsFormComponent } from '@/pages/ppm/milestone-form/slots-form/slots-form.component';
import { FundingDto, SlotDto } from '@/types';
import { PpmFundingSourceFormComponent } from '@/pages/ppm/milestone-form/funding-source-form/funding-source-form.component';
import { getFieldBuilderObjectFactory } from '@/components/form-builder/field-builder/utils/field-builder-factory';

interface getInspectAoFormObjectProps {
  store: AoRepository;
  hasSlots: Observable<boolean>;
  hasFundingSources: Observable<boolean>;
  currencyOptions?: SelectOption[];
  financialSourcesOptions?: SelectOption[];
}

export const getInspectAoFormObject = ({
  store,
  hasSlots,
  hasFundingSources,
  currencyOptions,
  financialSourcesOptions,
}: getInspectAoFormObjectProps): DynamicForm => {
  const ppmField: DynamicField<TextFieldProps> = {
    label: 'ao.form.ppm',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: of(true),
    props: {
      value: store.getNestedObservable('response', 'ppm.ref'),
      placeholder: 'ao.form.placeholders.ppm',
    },
  };

  const milestoneField: DynamicField<TextFieldProps> = {
    label: 'ao.form.milestone',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: of(true),
    props: {
      value: store.getNestedObservable('response', 'realisation.realisationEnvisagees'),
      placeholder: 'ao.form.placeholders.milestone',
    },
  };

  const refField: DynamicField<TextFieldProps> = {
    label: 'ao.form.ref',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: of(true),
    props: {
      value: store.getNestedObservable<string>('response', 'reference'),
      placeholder: 'ao.form.placeholders.ref',
    },
  };

  const nameField: DynamicField<TextFieldProps> = {
    label: 'ao.form.name',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: of(true),
    props: {
      value: store.getNestedObservable<string>('response', 'nom'),
      placeholder: 'ao.form.placeholders.name',
    },
  };

  const descriptionField: DynamicField<EditorFieldProps> = {
    label: 'ao.form.description',
    variant: FieldVariant.EDITOR,
    isRequired: false,
    isDisabled: of(true),
    props: {
      value: store.getNestedObservable<string>('response', 'description'),
      placeholder: 'ao.form.placeholders.description',
      height: '320px',
    },
  };

  const seperatedSlotsField: DynamicField<SwitchFieldProps> = {
    label: 'ao.form.seperatedSlots',
    variant: FieldVariant.SWITCH,
    isRequired: false,
    isDisabled: of(true),
    props: {
      checked: store.getNestedObservable<boolean>('response', 'lotSepares'),
      placeholder: 'ao.form.placeholders.seperatedSlots',
    },
  };

  //extra for display ********************************************************************************************
  const slotsField: DynamicField<CustomFieldProps> = {
    label: 'ao.form.slots',
    variant: FieldVariant.CUSTOM,
    isDisabled: of(true),
    isHidden: hasSlots.pipe(map((value: boolean) => !value)),
    props: {
      component: PpmSlotsFormComponent,
      value: store.getNestedObservable<SlotDto[]>('response.realisation.lots'),
      disabled: of(true),
      injectKeys: ['value', 'disabled'],
    },
  };

  const fundingSourcesField: DynamicField<CustomFieldProps> = {
    label: 'ao.form.funding',
    variant: FieldVariant.CUSTOM,
    isDisabled: of(true),
    isHidden: hasFundingSources.pipe(map((value: boolean) => !value)),
    props: {
      component: PpmFundingSourceFormComponent,
      disabled: of(true),
      currencyOptions: currencyOptions,
      financialSourcesOptions: financialSourcesOptions,
      value: store.getNestedObservable<FundingDto[]>('response.realisation.financements'),
      injectKeys: ['value', 'disabled', 'currencyOptions', 'financialSourcesOptions'],
    },
  };

  const acField: DynamicField<TextFieldProps> = {
    label: 'ao.form.ac',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: of(true),
    props: {
      value: store.getNestedObservable('response', 'ppm.ac.nom'),
    },
  };

  const passingModeField: DynamicField<TextFieldProps> = {
    label: 'ao.form.passingMode',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: of(true),
    props: {
      value: store.getNestedObservable('response', 'realisation.modePassation.libelle'),
    },
  };

  const passingModeTypeField: DynamicField<TextFieldProps> = {
    label: 'ao.form.portee',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: of(true),
    props: {
      value: store.getNestedObservable('response', 'realisation.modePassation.type'),
    },
  };

  const marketTypeField: DynamicField<TextFieldProps> = {
    label: 'ao.form.marketType',
    variant: FieldVariant.TEXT,
    isRequired: true,
    isDisabled: of(true),
    isHidden: store.getNestedObservable<boolean>('response', 'realisation').pipe(map(r => !r)),
    props: {
      value: store.getNestedObservable('response', 'realisation.typeMarcher.description'),
    },
  };

  return {
    title: 'ao.singular',
    description: 'ao.singular',
    orientation: 'horizontal',
    isHeaderHidden: true,
    grids: [
      {
        title: '',
        isHeaderHidden: true,
        gridItems: [
          {
            fields: [ppmField, milestoneField],
          },
          { fields: [acField, passingModeField] },
          {
            fields: [passingModeTypeField, marketTypeField],
          },
          {
            fields: [slotsField],
          },
          {
            fields: [fundingSourcesField],
          },
          { fields: [refField, nameField] },
          {
            fields: [seperatedSlotsField],
          },
          { fields: [descriptionField] },
        ],
      },
    ],
  };
};
